import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Footer from "../components/landing/Footer";
import Navigation from "../components/landing/Navigation";
import ModelViewer from "../components/stage/ModelViewer";
import { api, BASE } from "../services/api";

function resolveUrl(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

function ImageGallery({ images }) {
    const [idx, setIdx] = useState(0);
    if (!images?.length) return null;
    const prev = () => setIdx(i => (i - 1 + images.length) % images.length);
    const next = () => setIdx(i => (i + 1) % images.length);
    return (
        <div className="space-y-3">
            <div className="relative rounded-2xl overflow-hidden bg-nova-black/50 aspect-video">
                <img src={resolveUrl(images[idx])} alt="" className="w-full h-full object-cover" />
                {images.length > 1 && (
                    <>
                        <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors">
                            <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                            {images.map((_, i) => (
                                <button key={i} onClick={() => setIdx(i)}
                                    className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-nova-cyan scale-125" : "bg-white/30 hover:bg-white/50"}`} />
                            ))}
                        </div>
                    </>
                )}
            </div>
            {images.length > 1 && (
                <div className="grid grid-cols-6 gap-2">
                    {images.map((url, i) => (
                        <button key={i} onClick={() => setIdx(i)}
                            className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${i === idx ? "border-nova-cyan/70" : "border-transparent opacity-40 hover:opacity-70"}`}>
                            <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function VizualizaciaDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [model, setModel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allModels, setAllModels] = useState([]);

    useEffect(() => {
        Promise.all([api.getModels3d()])
            .then(([models]) => {
                setAllModels(models);
                const found = models.find(m => m.id === id);
                if (!found) { navigate("/vizualizacie", { replace: true }); return; }
                setModel(found);
            })
            .catch(() => navigate("/vizualizacie", { replace: true }))
            .finally(() => setLoading(false));
    }, [id, navigate]);

    const ext      = model?.file_url?.split(".").pop().toUpperCase() || "3D";
    const hasBody  = model?.body && model.body !== "<p></p>" && model.body.trim() !== "";
    const hasImgs  = model?.images?.length > 0;
    const others   = allModels.filter(m => m.id !== id).slice(0, 3);

    if (loading) {
        return (
            <div className="min-h-screen bg-nova-black flex items-center justify-center">
                <div className="font-mono text-sm text-zinc-600 animate-pulse">Načítavam…</div>
            </div>
        );
    }

    if (!model) return null;

    return (
        <div className="min-h-screen bg-nova-black text-white">
            <SEO
                title={`${model.title} — 3D Vizualizácia`}
                description={model.description || "Interaktívna 3D vizualizácia KB MUSIC."}
                canonical={`/vizualizacie/${model.id}`}
            />

            <Navigation />

            <main className="max-w-7xl mx-auto px-6 sm:px-12 pt-32 pb-12">

                {/* Title row */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8"
                >
                    <div className="flex items-start justify-between gap-6 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2.5 mb-3 flex-wrap">
                                <span className="font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-nova-cyan/15 text-nova-cyan border border-nova-cyan/25">
                                    {ext}
                                </span>
                                {model.tags?.map(t => (
                                    <span key={t} className="font-mono text-[9px] px-2 py-0.5 rounded-sm bg-white/5 text-zinc-500 border border-white/8">
                                        {t}
                                    </span>
                                ))}
                            </div>
                            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-3">
                                {model.title}
                            </h1>
                            {model.description && (
                                <p className="text-zinc-400 text-base max-w-2xl leading-relaxed">{model.description}</p>
                            )}
                        </div>
                        <Link to="/#contact"
                            className="shrink-0 inline-flex items-center gap-2 h-11 px-6 bg-nova-cyan text-black font-semibold text-sm rounded-xl hover:bg-nova-cyan/90 transition-all hover:shadow-[0_0_24px_rgba(0,153,255,0.3)]">
                            Objednať vizualizáciu
                        </Link>
                    </div>
                </motion.div>

                {/* 3D Viewer — full width, tall */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="rounded-2xl overflow-hidden border border-white/8 mb-10"
                    style={{ height: "clamp(360px, 60vh, 680px)" }}
                >
                    <ModelViewer fileUrl={model.file_url} height="100%" showControls />
                </motion.div>

                {/* Images + body */}
                {(hasImgs || hasBody) && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className={`mb-16 ${hasImgs && hasBody ? "grid lg:grid-cols-2 gap-10" : ""}`}
                    >
                        {hasImgs && <ImageGallery images={model.images} />}

                        {hasBody && (
                            <div
                                className="prose prose-invert prose-sm max-w-none
                                    prose-headings:font-heading prose-headings:tracking-tight
                                    prose-h2:text-xl prose-h3:text-lg
                                    prose-p:text-zinc-400 prose-p:leading-relaxed
                                    prose-li:text-zinc-400
                                    prose-a:text-nova-cyan prose-a:no-underline hover:prose-a:underline
                                    prose-strong:text-white"
                                dangerouslySetInnerHTML={{ __html: model.body }}
                            />
                        )}
                    </motion.div>
                )}

                {/* Other visualizations */}
                {others.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="mb-16"
                    >
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-5">Ďalšie vizualizácie</div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {others.map(m => {
                                const cover = m.images?.[0] || m.thumbnail_url || "";
                                return (
                                    <Link key={m.id} to={`/vizualizacie/${m.id}`}
                                        className="group rounded-xl border border-white/8 hover:border-white/20 bg-nova-surface/20 overflow-hidden transition-all">
                                        <div className="h-36 relative overflow-hidden bg-nova-black/50">
                                            {cover
                                                ? <img src={resolveUrl(cover)} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                                : <div className="w-full h-full bg-nova-surface/50" />
                                            }
                                            <div className="absolute inset-0 bg-gradient-to-t from-nova-black/60 to-transparent" />
                                        </div>
                                        <div className="p-4">
                                            <div className="font-heading font-bold text-white text-sm truncate">{m.title}</div>
                                            {m.description && <div className="text-zinc-600 text-xs mt-0.5 line-clamp-2">{m.description}</div>}
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </motion.div>
                )}

                {/* CTA */}
                <div className="border border-white/8 rounded-xl p-8 sm:p-12 bg-nova-surface/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-8">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-3">/ Záujem o vizualizáciu vášho eventu?</div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mb-2">
                            Pripravíme 3D návrh presne pre vaše miesto
                        </h2>
                        <p className="text-zinc-400 text-sm max-w-lg">
                            Pošlite nám pôdorys miesta a my navrhneme optimálne osvetlenie, zvuk a LED setup šitý na mieru.
                        </p>
                    </div>
                    <Link to="/#contact"
                        className="shrink-0 inline-flex items-center gap-2 h-11 px-6 bg-nova-cyan text-black font-semibold text-sm rounded-md hover:saturate-125 transition-all hover:shadow-[0_0_24px_rgba(0,153,255,0.38)]">
                        Kontaktovať KB MUSIC
                    </Link>
                </div>
            </main>

            <Footer />
        </div>
    );
}
